import React, { useState, useEffect } from 'react';
import { graphql, type HeadFC, type PageProps, type HeadProps } from 'gatsby';
import Layout from "components/layout";
import type { GatsbyCreatePageContext } from "types/gatsby-create-page-context";
import Seo from "components/seo";
import { useLocation } from '@reach/router';

// import components
import DebugPageData from 'components/partials/debug-page-data';
import StandardContent from 'components/partials/standard-content';
import TabbedBanner from 'components/partials/page_headers/tabbed-banner';
import SearchFieldRow from "components/partials/search-field-row";
import Accordion from 'components/partials/accordion';
import ContentBlocksBuilder from 'helpers/builders/content-blocks-builder';
import HelpCenterTopic from 'components/partials/help-center-topic';
import FaqSearchResults from 'helpers/help-center/faq-search-result';

// import types
import { FilterButton } from 'types/filter-button';
import { ContentBlocks } from 'types/content-blocks';

// import helpers
import LinkListCardsCollectionBuilder from 'helpers/builders/link-list-cards-collection-builder';
import { getAccordionFaq } from 'helpers/get-accordion-faq';
import { getAllFaqs, searchAllFaqs } from 'helpers/help-center/search';
import { getLinkById } from 'hooks/use-generate-site-link';

const HelpCenterTemplate: React.FC<PageProps<Queries.HelpCenterQuery, GatsbyCreatePageContext>> = ({ data, pageContext }) => {

    const location = useLocation();

    const params = new URLSearchParams(location.search);

    const isContactUsActive = params.get('tab') === 'contact-us';

    const allFaqs = getAllFaqs(data.datoCmsHelpCenter as Queries.HelpCenterFieldsFragment);

    const initialSearchTerm = params.get('search') || '';

    const [searchTerm, setSearchTerm] = useState(initialSearchTerm);

    let initialMatchingSearchFaqs = searchAllFaqs(allFaqs, initialSearchTerm);

    const [matchingSearchFaqs, setMatchingSearchFaqs] = useState(initialMatchingSearchFaqs);

    const handleFilterClicked = (id: string) => {

        // the set state method already knows the value of the previous state, so we can use it to update the state
        // prevFilters is the previous state of the tabbedFilters to help clarify the code
        setTabbedFilters(prevFilters => prevFilters.map(filter => {

            // Check if this is the filter we need to update
            if (filter.id === id) {
                // set active to true
                return { ...filter, isActive: true };
            } else {
                // set active to false
                return { ...filter, isActive: false };
            }
        }));
    };

    const handleKeywordChange = (newKeyword: string) => {
        setSearchTerm(newKeyword);
    };

    const initialFilters: FilterButton[] = [
        {
            id: "help-support",
            buttonText: "Help & Support",
            modifierClass: "help-support",
            slug: "",
            isActive: !isContactUsActive,
            onClick: () => handleFilterClicked("help-support")
        },
        {
            id: "contact-us",
            buttonText: "Contact Us",
            modifierClass: "contact-us",
            slug: "contact-us",
            isActive: isContactUsActive,
            onClick: () => handleFilterClicked("contact-us")
        }
    ];

    let activeFilter = getActiveFilter(initialFilters);

    const [tabbedFilters, setTabbedFilters] = useState(initialFilters);

    const [activeTab, setActiveTab] = useState(activeFilter.id);

    // Update the active tab when the active filter changes
    useEffect(() => {
        let activeFilter = getActiveFilter(tabbedFilters);

        setActiveTab(activeFilter.id);

        // Update the URL
        const url = new URL(window.location.href);

        const params = new URLSearchParams(url.search);

        if (activeFilter.slug) {
            params.set('tab', activeFilter.slug);
        } else {
            params.delete('tab');
        }

        url.search = params.toString();

        window.history.replaceState({}, '', url.toString());

    }, [tabbedFilters]);

    // check if search term updated then get new results
    useEffect(() => {
        setMatchingSearchFaqs(searchAllFaqs(allFaqs, searchTerm));
    }, [searchTerm]);

    let faqs = data.datoCmsHelpCenter?.faqs as Queries.FaqBlockFragment[] || [];

    let accordionPropsData = getAccordionFaq(faqs);

    return (
        <Layout navSection={pageContext.navSection} breadcrumbs={pageContext.breadcrumbs} currentPageId={pageContext.id}>
            <DebugPageData
                title={'matchingSearchFaqs'}
                pageTemplate={""}
                templateName={""}
                jsonData={JSON.stringify(matchingSearchFaqs, null, 2)} />
            <TabbedBanner title={data.datoCmsHelpCenter?.title ?? 'Error: No Title'} filterButtons={tabbedFilters} />
            {activeTab == "help-support" &&
                <>
                    <SearchFieldRow header="Search Help & Support" label="Keyword Search" buttonText="Search" searchParameter='search' initialSearch={searchTerm} onKeywordChange={handleKeywordChange} />

                    {searchTerm && matchingSearchFaqs.length === 0 ? (
                        <StandardContent content={"No results found."} />
                    ) : (
                        <FaqSearchResults matchingSearchFaqs={matchingSearchFaqs} />
                    )}
                    <ContentBlocksBuilder contentBlocks={data.datoCmsHelpCenter?.content?.content as ContentBlocks} />
                    {accordionPropsData && <Accordion {...accordionPropsData} />}
                    <LinkListCardsCollectionBuilder linkListCardsCollectionData={data.datoCmsHelpCenter?.linkListCards as Queries.LinkListCardsCollectionBlockFragment} services={data.datoCmsHelpCenter?.services as Queries.ServiceFieldsFragment[] || []} />
                    {data.datoCmsHelpCenter?.helpCenterTopics && data.datoCmsHelpCenter.helpCenterTopics.length > 0 &&
                        <StandardContent>
                            <h2 className='h1'>Browse By Topic</h2>
                        </StandardContent>
                    }
                    {data.datoCmsHelpCenter?.helpCenterTopics
                        ?.filter((topic) => topic !== null && topic !== undefined)
                        .map((topic, index) => (
                            <HelpCenterTopic key={index} topic={topic} />
                        ))
                    }
                </>
            }
            {activeTab == "contact-us" &&
                <StandardContent content={data.datoCmsHelpCenter?.contactInfo?.content || ""} />
            }
        </Layout>
    )
};

export default HelpCenterTemplate;

export const Head: HeadFC<Queries.HelpCenterQuery, GatsbyCreatePageContext> = (props: HeadProps<Queries.HelpCenterQuery, GatsbyCreatePageContext>) => {

    const itemTitle = props.data.datoCmsHelpCenter?.title ?? 'Unknown Help';

    const pageTitle = props.data.datoCmsSignatureEvent
        ? `${itemTitle} for ${props.data.datoCmsSignatureEvent.title}`
        : itemTitle;

    const canonicalLink = getCanonicalLinkIfApplicable(props.data);

    return (
        <Seo title={pageTitle} pageSeoMetaTags={props.data.datoCmsHelpCenter?.seoMetaTags?.tags}>
            {canonicalLink &&
                <link rel="canonical" href={canonicalLink} />
            }
        </Seo>
    );
};

export const query = graphql`
    query HelpCenter($id: String!, $signatureEventChild: Boolean! = false, $signatureEventId: String) {
            
        datoCmsHelpCenter(id: { eq: $id }) {
            ...HelpCenterFields
        }

        datoCmsSignatureEvent(id: { eq: $signatureEventId }) @include(if: $signatureEventChild) {
            id
            title
        }
    }
`

const getCanonicalLinkIfApplicable = (data: Queries.HelpCenterQuery) => {
    let path = getLinkById(data.datoCmsHelpCenter?.id ?? null, data.datoCmsSignatureEvent?.id ?? null) ;
    if (path !== null) {
        const location = useLocation();
        path = `${location?.origin ?? ''}${path}`;
    }
    
    return path;
};

const getActiveFilter = (filters: FilterButton[]): FilterButton => {

    // return the filter where isActive = true
    return filters.filter(filter => filter.isActive)[0];

}
